import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ColorPicker } from 'primereact/colorpicker';
import { Slider } from 'primereact/slider';
import { InputText } from 'primereact/inputtext';

// Editor de atributos de forma para o MapBox (espelha ShapesAttributesEditor do
// mapa Google). Emite um patch plano via onApply — não depende de objeto nativo.
// `shape` é um item de drawnShapes (MapboxMap).
const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

export default function ShapeAttributesEditor({ show, shape, onHide, onApply }) {
    const [name, setName] = useState('');
    const [fillColor, setFillColor] = useState('#3bb2d0');
    const [fillOpacity, setFillOpacity] = useState(0.5);
    const [strokeColor, setStrokeColor] = useState('#000000');
    const [strokeWeight, setStrokeWeight] = useState(3);
    const [radiusMeters, setRadiusMeters] = useState(0);
    // Texto dos campos de código hex — separado da cor aplicada para o usuário poder
    // digitar um valor incompleto sem que a forma mude a cada tecla.
    const [fillColorHex, setFillColorHex] = useState('#3bb2d0');
    const [strokeColorHex, setStrokeColorHex] = useState('#000000');

    // Ressincroniza o formulário sempre que a forma em edição muda.
    useEffect(() => {
        if (!shape) return;
        const fill = shape.fillColor ?? '#3bb2d0';
        const stroke = shape.strokeColor ?? '#000000';
        setName(shape.name ?? '');
        setFillColor(fill);
        setFillColorHex(fill);
        setFillOpacity(shape.fillOpacity ?? 0.5);
        setStrokeColor(stroke);
        setStrokeColorHex(stroke);
        setStrokeWeight(shape.strokeWeight ?? 3);
        setRadiusMeters(Math.round(shape.radiusMeters ?? 0));
    }, [shape]);

    const isCircle = shape?.type === 'Círculo';

    // O ColorPicker do PrimeReact devolve o hex sem '#'.
    const withHash = (v) => (v?.startsWith('#') ? v : `#${v}`);

    const pickColor = (setColor, setHex) => (e) => {
        const value = withHash(e.value);
        setColor(value);
        setHex(value);
    };

    // Código hex digitado: aceita com ou sem '#' e só aplica quando o valor é válido.
    const typeColor = (setColor, setHex) => (e) => {
        const value = e.target.value && !e.target.value.startsWith('#')
            ? `#${e.target.value}`
            : e.target.value;

        setHex(value);
        if (HEX_COLOR_REGEX.test(value)) {
            setColor(value);
        }
    };

    const apply = () => {
        onApply({
            name,
            fillColor,
            fillOpacity: Number(fillOpacity),
            strokeColor,
            strokeWeight: Number(strokeWeight),
            radiusMeters: Number(radiusMeters),
        });
    };

    return (
        <Dialog header="Editar Forma" visible={show} style={{ width: '32rem', maxWidth: '90vw' }} modal onHide={onHide}>
            <div className="form-group">
                <label htmlFor="shapeName">Rótulo</label>
                <InputText
                    id="shapeName"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </div>

            <div className="form-group">
                <div className="d-flex">
                    <div style={{ width: '50%' }}>
                        <label className="d-block" htmlFor="shapeFillColorHex">Cor de Preenchimento</label>
                        <div className="d-flex align-items-center">
                            {/* `key` força o ColorPicker a reler o valor quando a cor vem do campo hex. */}
                            <ColorPicker
                                key={fillColor}
                                value={fillColor}
                                onChange={pickColor(setFillColor, setFillColorHex)}
                            />
                            <InputText
                                id="shapeFillColorHex"
                                className="form-control ml-2"
                                style={{ width: '90px' }}
                                maxLength={7}
                                placeholder="XXXXXX"
                                value={fillColorHex}
                                onChange={typeColor(setFillColor, setFillColorHex)}
                            />
                        </div>
                    </div>
                    <div style={{ width: '50%' }}>
                        <label className="d-block" htmlFor="shapeStrokeColorHex">Cor da Borda</label>
                        <div className="d-flex align-items-center">
                            <ColorPicker
                                key={strokeColor}
                                value={strokeColor}
                                onChange={pickColor(setStrokeColor, setStrokeColorHex)}
                            />
                            <InputText
                                id="shapeStrokeColorHex"
                                className="form-control ml-2"
                                style={{ width: '90px' }}
                                maxLength={7}
                                placeholder="XXXXXX"
                                value={strokeColorHex}
                                onChange={typeColor(setStrokeColor, setStrokeColorHex)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-group">
                {/* O Slider do PrimeReact 6 trunca o valor para inteiro (Math.floor),
                    então trabalhamos em porcentagem (10–100) e convertemos para 0.1–1. */}
                <label className="d-block">Opacidade do Preenchimento: {Math.round(fillOpacity * 100)}%</label>
                <Slider
                    value={Math.round(fillOpacity * 100)}
                    onChange={(e) => setFillOpacity(e.value / 100)}
                    min={10}
                    max={100}
                    step={10}
                />
            </div>

            <div className="form-group">
                <label className="d-block">Espessura da Borda: {strokeWeight}</label>
                <Slider value={strokeWeight} onChange={(e) => setStrokeWeight(e.value)} min={1} max={10} step={1} />
            </div>

            {isCircle && (
                <div className="form-group">
                    <label htmlFor="shapeRadius">Raio (m)</label>
                    <InputText
                        id="shapeRadius"
                        type="number"
                        min="1"
                        className="form-control"
                        value={radiusMeters}
                        onChange={(e) => setRadiusMeters(e.target.value)}
                    />
                </div>
            )}

            <button className="btn btn-sm btn-primary btn-block mt-2" onClick={apply}>
                Aplicar
            </button>
        </Dialog>
    );
}
